import type { AnalyticsReporter, ErrorReporter } from '@fuuze/shared';
import { enableMapSet } from 'immer';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { PosthogReporter } from '../services/posthog.service';
import { SentryReporter } from '../services/sentry.service';
import { usePlayerStore } from './player.store';

enableMapSet();

type StoreState = {
  // Store the value of the last search navigation to be able to restore it when the user clicks on the search icon
  lastSearchNavigation?: string;
  search?: string;
  podcastSelected: { _id: string; slug: string };
  // Store the path before login to redirect the user to initial path after he logs in, instead of redirecting him to the home page
  pathBeforeLogin?: string;
  errorReporter: ErrorReporter;
  analyticsReporter: AnalyticsReporter;
};

type StoreActions = {
  setLastSearchNavigation: (lastSearchNavigation?: string) => void;
  setSearch: (search: string) => void;
  setPodcastSelected: (_id: string, slug: string) => void;
  reset: () => void;
  setPathBeforeLogin: (path: string) => void;
};

type Store = StoreState & StoreActions;

const initialState: StoreState = {
  lastSearchNavigation: undefined,
  search: undefined,
  podcastSelected: { _id: '', slug: '' },
  pathBeforeLogin: undefined,
  errorReporter: new SentryReporter(),
  analyticsReporter: new PosthogReporter(),
};

export const useStore = create<Store>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,
      setLastSearchNavigation: lastSearchNavigation => set({ lastSearchNavigation }),
      setSearch: search => set({ search }),
      setPodcastSelected: (_id, slug) => set({ podcastSelected: { _id, slug } }),
      reset: () => set(() => ({ ...initialState })),
      setPathBeforeLogin: path => set({ pathBeforeLogin: path }),
    })),
  ),
);

export function useErrorReporter() {
  const errorReporter = useStore(state => state.errorReporter);
  return errorReporter;
}

export function useAnalyticsReporter() {
  const analyticsReporter = useStore(state => state.analyticsReporter);
  return analyticsReporter;
}

if (import.meta.env.DEV) {
  mountStoreDevtool('Store', useStore);
  mountStoreDevtool('PlayerStore', usePlayerStore);
}
